<template>
  <div>
    <v-card-title>
      <v-select
        v-model="filterType"
        :items="filterTypes()"
        label="Filter"
        item-key="id"
        item-text="name"
        item-value="id"
        style="margin-top: 20px; max-width: 200px;"
        append-icon="filter_alt"
        @input="onFilterTypeChange()"
      >
        <template v-slot:item="{ item }">
          <v-icon>
            {{ item.icon }}
          </v-icon>
          {{ item.name }}
        </template>
        <template v-slot:selection="{ item, index }">
          <v-icon>
            {{ item.icon }}
          </v-icon>
          {{ item.name }}
        </template>
      </v-select>
      <v-btn
        class="selectAllBtn"
        @click="selectAll"
      >
        select all
      </v-btn>
      <v-btn
        class="selectAllBtn"
        @click="selectNone"
      >
        select none
      </v-btn>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter"
        style="margin-top: 20px;"
      />
    </v-card-title>

    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          color="secondary"
          fab
          dark
          absolute
          top
          left
          @click.stop="refresh"
          v-on="on"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
      </template>
      <span>Refresh</span>
    </v-tooltip>

    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          v-if="selectedJobIds.length > 0"
          color="secondary"
          fab
          dark
          absolute
          top
          right
          @click.stop="showDialog(selectedJobIds.join(','))"
          v-on="on"
        >
          <v-icon>done</v-icon>
        </v-btn>
      </template>
      <span>Solve {{ selectedJobIds.length }} Job(s)</span>
    </v-tooltip>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
      :rows-per-page-items="[25,50,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td
            @click.stop="() => null"
          >
            <v-checkbox
              :key="props.item.id"
              v-model="selectedJobIds"
              :value="props.item.id"
              multiple
              class="mt-3"
            />
          </td>
          <td>
            <DateDisplay
              :date="props.item.sk.replace('TIMESTAMP#', '')"
              :show-time="true"
            />
          </td>
          <td>
            {{ props.item.message_id }}
          </td>
          <td>
            {{ props.item.message_attributes }}
          </td>
          <td>
            {{ props.item.event_source_arn }}
          </td>
          <td>
            <v-btn
              dark
              flat
              block
              color="primary"
              @click.stop="() => setDebugObject(props.item.event)"
            >
              <v-icon
                class="pr-2"
              >
                adb
              </v-icon>
              Debug
            </v-btn>
          </td>
          <td>
            {{ props.item.user_id }}
          </td>
          <td>
            {{ props.item.payout_id }}
          </td>
          <td>
            {{ props.item.security_id }}
          </td>
          <td>
            {{ props.item.resolved_by }}
            <DateDisplay
              :date="props.item.resolved_at"
            />
          </td>
          <td class="text-xs">
            <v-btn
              v-if="filterType === 'open'"
              dark
              flat
              icon
              color="primary"
              @click.stop="() => showDialog(props.item.id)"
            >
              <v-icon>done</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <ResolveDialog
      key="resolve_dialog"
      :visible="isDialogActive()"
      @close="hideDialog()"
      @submit="onResolve()"
    />

    <v-snackbar
      v-model="snackbarShow"
      :color="snackbarColor"
      :auto-height="true"
    >
      <ul v-if="snackbarErrors">
        <li
          v-for="snackbarError in snackbarErrors"
          :key="snackbarError.message"
        >
          {{ snackbarError.message }}
        </li>
      </ul>

      <div
        v-if="snackbarMessage"
      >
        {{ snackbarMessage }}
      </div>

      <v-btn
        color="primary"
        text
        @click="snackbarShow = false"
      >
        Close
      </v-btn>
    </v-snackbar>
    <DebugDialog
      :visible="isDebugDialogActive"
      :debug-object="debugObject"
      :title="debugTitle"
      @close="isDebugDialogActive = false"
    />
  </div>
</template>

<script>

import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay';
import ResolveDialog from '@/components/payoutsAndTaxes/payout/dialogs/failedJob/ResolveDialog';
import DebugDialog from '@/components/payoutsAndTaxes/payout/dialogs/shared/DebugDialog';

export default {
  name: 'FailedJobs',
  components: {
    DebugDialog,
    DateDisplay,
    ResolveDialog,
  },
  data() {
    return {
      snackbarShow: false,
      snackbarMessage: '',
      snackbarColor: 'error',
      snackbarErrors: [],
      currentDeleteItem: 0,
      selectedJobIds: [],
      dialog: false,
      filterType: 'open',
      isLoading: false,
      debugObject: {},
      debugTitle: '',
      isDebugDialogActive: false,
      isHolderDeleteDialogActive: false,
      pagination: {
        rowsPerPage: 25,
        sortBy: 'sk',
        descending: true,
      },
      search: '',
      headers: [
        { text: 'Select', value: 'checked' },
        { text: 'Created At', value: 'sk' },
        { text: 'Message ID', value: 'message_id' },
        { text: 'Message Attributes', value: 'message_attributes' },
        { text: 'Event Source Arn', value: 'event_source_arn' },
        { text: 'Event', value: 'event' },
        { text: 'User ID', value: 'user_id' },
        { text: 'Payout ID', value: 'payout_id' },
        { text: 'Security ID', value: 'security_id' },
        { text: 'Resolved By', value: 'resolved_by' },
        { text: 'Actions', value: 'actions' },
      ],
    };
  },
  computed: {
    data() {
      if (this.filterType === 'open') {
        return this.$store.getters.getPayoutsFailedJobOpen;
      }

      if (this.filterType === 'resolved') {
        return this.$store.getters.getPayoutsFailedJobResolved;
      }

      return [];
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.data :
        this.data.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    filterTypes() {
      return [
        {
          id: 'open',
          icon: 'error',
          name: 'Open',
        },
        {
          id: 'resolved',
          icon: 'done',
          name: 'Resolved',
        },
      ];
    },
    getTypeByActiveFilter() {
      if (this.filterType === 'open') {
        return 'fetchPayoutFailedJobOpen';
      }

      if (this.filterType === 'resolved') {
        return 'fetchPayoutFailedJobResolved';
      }

      return false;
    },
    onFilterTypeChange() {
      this.refresh();
    },
    refresh() {
      this.$store.dispatch(this.getTypeByActiveFilter());
    },
    isDialogActive() {
      return this.dialog;
    },
    showDialog(jobId) {
      this.currentDeleteItem = jobId;
      this.dialog = true;
      this.$forceUpdate();
    },
    hideDialog() {
      this.dialog = false;
      this.$forceUpdate();
    },
    setDebugObject(object) {
      this.debugObject = object;
      this.debugTitle = 'Debug Information';
      this.isDebugDialogActive = true;
    },
    selectAll() {
      this.selectedJobIds = this.filteredItems.map(item => item.id);
    },
    selectNone() {
      this.selectedJobIds = [];
    },
    onResolve() {
      this.$store
        .dispatch('callPayoutFailedJobResolve', {
          id: this.currentDeleteItem,
          resolved: true,
        })
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Failed Job(s) set to resolved.';
          this.snackbarColor = 'success';

          this.refresh();
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
  },
};
</script>
<style lang="scss">
.selectAllBtn {
  margin-left: 2%;
  color: blue;
}
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/FailedJobs.vue